// packages/shared/src/services/algoliaSearchService.ts
import { Court, CourtFilters, PaginatedCourtsResult } from '../types';

// Define Algolia-specific types (avoid naming conflict with config)
export interface AlgoliaServiceConfig {
  appId: string;
  searchApiKey: string;
  indices: {
    courts: string;
  };
}

export interface AlgoliaSearchParams {
  query: string;
  page: number;
  hitsPerPage: number;
  filters?: string;
  facets?: string[];
  sortBy?: string;
}

export interface AlgoliaSearchResult {
  courts: Court[];
  totalHits: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  facets: {
    cities: { [key: string]: number };
    countries: { [key: string]: number };
    facilityTypes: { [key: string]: number };
    courtSurfaces: { [key: string]: number };
    courtFees: { [key: string]: number };
  };
}

export class AlgoliaSearchService {
  private static instance: AlgoliaSearchService;
  private algoliaClient: any = null;
  private courtsIndex: any = null;
  private isInitialized = false;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): AlgoliaSearchService {
    if (!AlgoliaSearchService.instance) {
      AlgoliaSearchService.instance = new AlgoliaSearchService();
    }
    return AlgoliaSearchService.instance;
  }

  /**
   * Initialize Algolia client with configuration
   * This should be called from the web package where algoliasearch is available
   */
  public async initializeAlgolia(config: AlgoliaServiceConfig): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Dynamic import of algoliasearch (will be available in web package)
      const algoliaModule = await import('algoliasearch/lite');
      
      // Use type assertion to handle the dynamic import
      const algoliasearch = (algoliaModule as any).default || (algoliaModule as any);
      
      this.algoliaClient = algoliasearch(config.appId, config.searchApiKey);
      this.courtsIndex = { indexName: config.indices.courts };
      this.isInitialized = true;
      
      console.log('Algolia client initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Algolia client:', error);
      throw new Error('Algolia initialization failed');
    }
  }

  /**
   * Initialize with pre-created Algolia client (for static imports)
   * This method allows the web package to pass a pre-initialized client
   */
  public initializeWithClient(algoliaClient: any, courtsIndex: any): void {
    if (this.isInitialized) {
      console.log('Algolia already initialized, skipping...');
      return;
    }

    console.log('Initializing AlgoliaSearchService with pre-created client');
    console.log('Client:', algoliaClient);
    console.log('Index:', courtsIndex);

    this.algoliaClient = algoliaClient;
    this.courtsIndex = courtsIndex;
    this.isInitialized = true;
    
    console.log('✅ Algolia client initialized with pre-created client');
  }

  /**
   * Search courts using Algolia with advanced filtering and faceting
   */
  public async searchCourts(params: AlgoliaSearchParams): Promise<AlgoliaSearchResult> {
    if (!this.isInitialized || !this.courtsIndex) {
      throw new Error('Algolia not initialized. Call initializeAlgolia() first.');
    }

    try {
      // Build search options
      const searchOptions: any = {
        page: params.page,
        hitsPerPage: params.hitsPerPage,
        attributesToRetrieve: ['*'],
        facets: params.facets || [
          'location.city', 
          'location.country', 
          'details.facility_type', 
          'details.court_surface', 
          'pricing.category'
        ],
      };

      // Add filters if provided
      if (params.filters) {
        searchOptions.filters = params.filters;
      }

      // Determine index name
      let indexName = this.courtsIndex.indexName || 'courts';
      if (params.sortBy && params.sortBy !== 'courts_name_asc') {
        // Use replica index for sorting
        indexName = `${indexName}_${params.sortBy}`;
      }

      console.log('Direct browser-to-Algolia search:', {
        query: params.query,
        options: searchOptions,
        note: 'This query goes directly to Algolia CDN - no Firebase load!'
      });

      // Use v5 API
      const response = await this.algoliaClient.searchSingleIndex({
        indexName: indexName,
        searchParams: {
          query: params.query,
          ...searchOptions
        }
      });

      console.log('Algolia search response:', {
        nbHits: response.nbHits,
        page: response.page,
        nbPages: response.nbPages,
        facets: response.facets
      });

      // Transform the results
      const courts = response.hits.map((hit: any) => this.transformHitToCourt(hit));

      // Extract facets
      const facets = {
        cities: response.facets?.['location.city'] || {},
        countries: response.facets?.['location.country'] || {},
        facilityTypes: response.facets?.['details.facility_type'] || {},
        courtSurfaces: response.facets?.['details.court_surface'] || {},
        courtFees: response.facets?.['pricing.category'] || {},
      };

      return {
        courts,
        totalHits: response.nbHits,
        totalCount: response.nbHits,
        hasNextPage: response.page < response.nbPages - 1,
        hasPrevPage: response.page > 0,
        facets,
      };

    } catch (error) {
      console.error('Algolia search error:', error);
      throw new Error('Failed to search courts');
    }
  }

/**
   * Generic search method for any index
   * @param indexName - The name of the Algolia index to search
   * @param searchParams - Search parameters
   * @returns Promise with search results
   */
  public async searchIndex(indexName: string, searchParams: {
  query: string;
  page?: number;
  hitsPerPage?: number;
  filters?: string;
  facets?: string[];
  attributesToRetrieve?: string[];
  attributesToHighlight?: string[];
}): Promise<{
  hits: any[];
  nbHits: number;
  page: number;
  nbPages: number;
  hitsPerPage: number;
  facets?: any;
}> {
  if (!this.isInitialized || !this.algoliaClient) {
    throw new Error('Algolia not initialized. Call initializeAlgolia() first.');
  }

  try {
    const algoliaSearchParams: any = {
      page: searchParams.page || 0,
      hitsPerPage: searchParams.hitsPerPage || 20,
    };

    if (searchParams.filters) {
      algoliaSearchParams.filters = searchParams.filters;
    }

    if (searchParams.facets && searchParams.facets.length > 0) {
      algoliaSearchParams.facets = searchParams.facets;
    }

    if (searchParams.attributesToRetrieve) {
      algoliaSearchParams.attributesToRetrieve = searchParams.attributesToRetrieve;
    }

    if (searchParams.attributesToHighlight) {
      algoliaSearchParams.attributesToHighlight = searchParams.attributesToHighlight;
    }

    console.log(`Searching ${indexName} index:`, {
      query: searchParams.query,
      params: algoliaSearchParams
    });

    // Use v5 API
    const result = await this.algoliaClient.searchSingleIndex({
      indexName: indexName,
      searchParams: {
        query: searchParams.query,
        ...algoliaSearchParams
      }
    });
    
    return {
      hits: result.hits || [],
      nbHits: result.nbHits || 0,
      page: result.page || 0,
      nbPages: result.nbPages || 0,
      hitsPerPage: result.hitsPerPage || 20,
      facets: result.facets,
    };
  } catch (error) {
    console.error(`Error searching ${indexName} index:`, error);
    throw error;
  }
}
  /**
   * Convert CourtFilters to Algolia-compatible search parameters
   */
  public convertFiltersToAlgolia(filters: CourtFilters): AlgoliaSearchParams {
    const algoliaFilters = this.buildAlgoliaFilters(filters);
    const sortBy = this.buildAlgoliaSortBy(filters);

    return {
      query: filters.search || '',
      page: 0,
      hitsPerPage: 25,
      filters: algoliaFilters,
      facets: ['location.city', 'location.country', 'details.facility_type', 'details.court_surface', 'pricing.category'],
      sortBy
    };
  }

  /**
   * Get filter options (facets) for building UI dropdowns
   */
  public async getFilterOptions(): Promise<AlgoliaSearchResult['facets']> {
    if (!this.isInitialized || !this.courtsIndex) {
      throw new Error('Algolia not initialized. Call initializeAlgolia() first.');
    }

    try {
      // Use v5 API
      const response = await this.algoliaClient.searchSingleIndex({
        indexName: this.courtsIndex.indexName || 'courts',
        searchParams: {
          query: '',
          hitsPerPage: 0, // We only want facets, not results
          facets: ['location.city', 'location.country', 'details.facility_type', 'details.court_surface', 'pricing.category']
        }
      });

      return {
        cities: response.facets?.['location.city'] || {},
        countries: response.facets?.['location.country'] || {},
        facilityTypes: response.facets?.['details.facility_type'] || {},
        courtSurfaces: response.facets?.['details.court_surface'] || {},
        courtFees: response.facets?.['pricing.category'] || {},
      };
    } catch (error) {
      console.error('Failed to get filter options:', error);
      return {
        cities: {},
        countries: {},
        facilityTypes: {},
        courtSurfaces: {},
        courtFees: {},
      };
    }
  }

  /**
   * Health check method to verify Algolia connection
   */
  public async healthCheck(): Promise<boolean> {
    if (!this.isInitialized || !this.courtsIndex) {
      return false;
    }

    try {
      const response = await this.algoliaClient.searchSingleIndex({
        indexName: this.courtsIndex.indexName || 'courts',
        searchParams: {
          query: '',
          hitsPerPage: 1,
          attributesToRetrieve: ['objectID']
        }
      });
      return response !== null;
    } catch (error) {
      console.error('Algolia health check failed:', error);
      return false;
    }
  }

  /**
   * Get index settings for debugging
   */
  public async getIndexSettings() {
    if (!this.isInitialized || !this.courtsIndex) {
      throw new Error('Algolia not initialized. Call initializeAlgolia() first.');
    }

    try {
      // Note: getSettings might not be available in v5 searchSingleIndex
      // You may need to use a different approach or remove this method
      throw new Error('getSettings not supported in Algolia v5 searchSingleIndex API');
    } catch (error) {
      console.error('Failed to get index settings:', error);
      throw error;
    }
  }
  
  private transformHitToCourt(hit: any): Court {
    try {
      // Transform the Algolia hit back to Court type
      // Handle timestamp conversion if needed
      const court: Court = {
        id: hit.objectID || hit.id,
        name: hit.name || '',
        location: {
          address: hit.location?.address || '',
          city: hit.location?.city || '',
          country: hit.location?.country || '',
          coordinates: hit.location?.coordinates || [0, 0],
        },
        details: {
          facility_type: hit.details?.facility_type,
          total_courts: hit.details?.total_courts,
          court_surface: hit.details?.court_surface,
          lights: hit.details?.lights,
          indoor_courts: hit.details?.indoor_courts,
          telephone: hit.details?.telephone,
          website: hit.details?.website,
          url: hit.details?.url,
          google_maps_url: hit.details?.google_maps_url,
          matches_played: hit.details?.matches_played,
          court_fees: hit.details?.court_fees,
          court_type: hit.details?.court_type,
          region: hit.details?.region,
          postal_code: hit.details?.postal_code,
          indoor_courts_description: hit.details?.indoor_courts_description,
          amenities_raw: hit.details?.amenities_raw,
        },
        pricing: {
          hourly: hit.pricing?.hourly || 0,
          currency: hit.pricing?.currency || 'USD',
        },
        availability: {
          timezone: hit.availability?.timezone || 'UTC',
          schedule: hit.availability?.schedule || {},
        },
        amenities: hit.amenities || [],
        managerId: hit.managerId,
        createdAt: hit.createdAt ? new Date(hit.createdAt) : new Date(),
        updatedAt: hit.updatedAt ? new Date(hit.updatedAt) : new Date(),
      };

      return court;
    } catch (error) {
      console.error('Error transforming hit to court:', error, hit);
      throw error;
    }
  }

  private buildAlgoliaFilters(filters: CourtFilters): string {
    const filterParts: string[] = [];

    if (filters.city) {
      filterParts.push(`location.city:"${filters.city}"`);
    }
    if (filters.country) {
      filterParts.push(`location.country:"${filters.country}"`);
    }
    if (filters.facilityType) {
      filterParts.push(`details.facility_type:"${filters.facilityType}"`);
    }
    if (filters.courtSurface) {
      filterParts.push(`details.court_surface:"${filters.courtSurface}"`);
    }
    if (filters.lights !== undefined) {
      filterParts.push(`details.lights:${filters.lights}`);
    }
    if (filters.hasIndoorCourts) {
      filterParts.push(`details.indoor_courts > 0`);
    }
    if (filters.courtFees) {
      filterParts.push(`pricing.category:"${filters.courtFees}"`);
    }

    return filterParts.join(' AND ');
  }

  private buildAlgoliaSortBy(filters: CourtFilters): string {
    const direction = filters.sortOrder === 'desc' ? '_desc' : '_asc';
    
    switch (filters.sortBy) {
      case 'price':
        return `courts_price${direction}`;
      case 'total_courts':
        return `courts_total${direction}`;
      case 'name':
      default:
        return `courts_name${direction}`;
    }
  }
}